import {
	debounce,
	FormControl,
	Grid,
	InputAdornment,
	OutlinedInput,
	SelectChangeEvent,
	Stack,
	Typography,
} from '@mui/material';

import { useEffect, useMemo, useRef, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { ReactComponent as FilterIcon } from 'common/assets/images/filterwhite.svg';
import { ReactComponent as SearchOutlinedIcon } from 'common/assets/images/search-outline.svg';
import Banner from 'common/components/Banner/Banner';
import ButtonComp from 'common/components/Button/ButtonComp';
import SectionToolbar from 'common/components/SectionToolbar/SectionToolbar';
import { ViewType } from 'common/constants';
import { FeatureFlagsKeysEnum } from 'common/constants/FeatureFlags';
import { LOADING } from 'common/constants/index';
import {
	FILTER_BUTTON_LABEL,
	LANDING_PAGE_DROPDOWN_TYPE,
	PRC_LANDING_DROPDOWN_LOADING_MENU_ITEM,
	TAXONOMY_CURRENT_TAB_LIST,
	taxonomySecTools,
} from 'common/constants/TaxonomyConstants';
import { RiskReferenceTaxonomyTitle } from 'common/constants/TextContent';
import { RootState } from 'common/store/AppStore';
import {
	setRiskRefApplyFilterEnabled,
	setRiskRefApplyTaxonomyFilterFlag,
	setRiskRefSearchText,
	setRiskRefTaxonomyFilter,
	setTaxonomySelectedAppliedFilterList,
	setTaxonomyViewTypeValue,
} from 'common/store/reducer/taxonomyReducer';
import { toSentenceCase } from 'common/utils/utilFunctions';

import { useFeatureFlag } from 'hooks/useFeatureFlags';

import {
	IDataBaseOptionsResponse,
	IIsApiLoading,
	TaxonomySearchCount,
} from '../../api/type-def';
import {
	getCategoryOptionList,
	setTabIndexData,
} from '../../RiskRefTaxonomyUtils';

import PRCInventoryLandingHeaderDropDown from './PRCInventoryLandingHeaderDropDown';

import styles from './PRCInventoryLandingHeader.module.scss';

const { title, body } = RiskReferenceTaxonomyTitle;
const { filterIconColor } = styles;
const { DATABASE, CATEGORY } = LANDING_PAGE_DROPDOWN_TYPE;
const { IS_PRC_CARD_VIEW_ENABLED } = FeatureFlagsKeysEnum;
const { CARD, TABLE, HEATMAP } = ViewType;
const { REACT_APP_ENVIRONMENT } = process.env;

interface IPRCInventoryLandingHeaderProps {
	dataBaseOptionsList: IDataBaseOptionsResponse[];
	isApiLoading: IIsApiLoading;
	PRCEntityCount: TaxonomySearchCount;
	handleDataBaseSelectChange: (event: SelectChangeEvent<string>) => void;
	exportHandler: () => Promise<void>;
	onAdvanceFilter: () => Promise<void>;
	handleResetFilter: () => void;
}

const PRCInventoryLandingHeader = (props: IPRCInventoryLandingHeaderProps) => {
	const {
		dataBaseOptionsList,
		PRCEntityCount,
		isApiLoading,
		handleDataBaseSelectChange,
		exportHandler,
		onAdvanceFilter,
		handleResetFilter,
	} = props;
	const dispatch = useDispatch();
	const taxonomySelectedAppliedfilterKeys = useSelector(
		(state: RootState) => state.taxonomy.taxonomySelectedAppliedfilterKeys,
	);
	const riskRefTaxonomySourceFiltersKey = useSelector(
		(state: RootState) => state.taxonomy.riskRefTaxonomySourceFiltersKey,
	);
	const refSearchText = useSelector(
		(state: RootState) => state.taxonomy.riskRefSearchText,
	);
	const taxonomyViewType = useSelector(
		(state: RootState) => state.taxonomy.taxonomyViewTypeValue,
	);
	const tabIndex = useSelector(
		(state: RootState) => state.common.selectedTabIndex,
	);
	const isLDResponseExists: boolean = useSelector(
		(state: RootState) => state.common.isLDResponseAvailable,
	);
	const isPrcCardViewEnabled = useFeatureFlag(
		IS_PRC_CARD_VIEW_ENABLED,
		isLDResponseExists,
	);

	const searchvalueRef = useRef<any>(null);
	const moreMenuRef = useRef() as React.MutableRefObject<HTMLDivElement>;

	const [moreMenuAnchorEl, setMoreMenuAnchorEl] = useState<null | HTMLElement>(
		null,
	);
	const [dataBaseSelectValue, setDataBaseSelectValue] = useState<string>(
		PRC_LANDING_DROPDOWN_LOADING_MENU_ITEM[0].value,
	);
	const [categorySelectValue, setCategorySelectValue] = useState<string>(
		PRC_LANDING_DROPDOWN_LOADING_MENU_ITEM[0].value,
	);
	const [dataBaseOptionList, setDataBaseOptionList] = useState<
		IDataBaseOptionsResponse[]
	>(PRC_LANDING_DROPDOWN_LOADING_MENU_ITEM);
	const [categoryOptionList, setCategoryOptionList] = useState<
		IDataBaseOptionsResponse[]
	>(PRC_LANDING_DROPDOWN_LOADING_MENU_ITEM);

	const filterBtnLabel = useMemo(() => {
		let filterLabel = FILTER_BUTTON_LABEL;
		const appliedFilterNum = taxonomySelectedAppliedfilterKeys.length;
		if (appliedFilterNum > 0) {
			filterLabel += ` (${appliedFilterNum})`;
		}
		return filterLabel;
	}, [taxonomySelectedAppliedfilterKeys]);

	const handleClickMoreMenu = () => {
		setMoreMenuAnchorEl(moreMenuRef.current ? moreMenuRef?.current : null);
	};

	const handleCloseMoreMenu = () => {
		setMoreMenuAnchorEl(null);
	};

	const handleDatabaseChange = (event: SelectChangeEvent<string>) => {
		handleDataBaseSelectChange(event);
		searchvalueRef.current.value = '';
	};

	const handleClickFilterBtn = () => {
		if (!isApiLoading?.filters) {
			onAdvanceFilter();
		}
		handleCloseMoreMenu();
	};

	const handleToggleViewType = (type: ViewType) => {
		dispatch(setRiskRefTaxonomyFilter([]));
		dispatch(setTaxonomySelectedAppliedFilterList([]));
		dispatch(setRiskRefApplyFilterEnabled(false));
		dispatch(setRiskRefApplyTaxonomyFilterFlag(false));
		dispatch(setTaxonomyViewTypeValue(type));
	};

	const sectionToolBarInitFunc = () => {
		if (!isPrcCardViewEnabled) {
			dispatch(setTaxonomyViewTypeValue(TABLE));
		}
	};

	const debouncedSearchHandler = debounce((event: any) => {
		const text = event?.target?.value;
		dispatch(setRiskRefSearchText(text));
	}, 500);

	const handleCategoryChange = (event: any) => {
		const optionValue = event?.target?.value;
		const categoryIndex = TAXONOMY_CURRENT_TAB_LIST.findIndex(
			(option) => option === optionValue,
		);
		dispatch(setRiskRefApplyFilterEnabled(false));
		dispatch(setRiskRefApplyTaxonomyFilterFlag(false));
		dispatch(setRiskRefTaxonomyFilter([]));
		dispatch(setTaxonomySelectedAppliedFilterList([]));
		setTabIndexData(categoryIndex, REACT_APP_ENVIRONMENT, dispatch);
		handleResetFilter();
		// since policy category has no heatmap need to change to card or table view
		if (categoryIndex === 4 && taxonomyViewType === HEATMAP) {
			dispatch(setTaxonomyViewTypeValue(isPrcCardViewEnabled ? CARD : TABLE));
		}
	};

	useEffect(() => {
		if (!isApiLoading.count) {
			const categoryOptionList = getCategoryOptionList(PRCEntityCount);
			setDataBaseOptionList(dataBaseOptionsList);
			setCategoryOptionList(categoryOptionList);
			setDataBaseSelectValue(riskRefTaxonomySourceFiltersKey);
			setCategorySelectValue(TAXONOMY_CURRENT_TAB_LIST[tabIndex]);
		}
	}, [isApiLoading]);

	return (
		<Stack>
			<Banner>
				<Stack p={2} className={styles['prc-landing-header-wrapper']}>
					<Grid>
						<Typography className={styles['prc-landing-header-title']}>
							{title}
						</Typography>
						<Typography mt={0} className={styles['prc-landing-header-content']}>
							{body}
						</Typography>
					</Grid>
					<Grid
						container
						className={styles['prc-landing-header-input-comp-wrapper']}
						alignItems="center"
					>
						{/* Database dropdown */}
						<PRCInventoryLandingHeaderDropDown
							type={DATABASE}
							value={dataBaseSelectValue}
							optionList={dataBaseOptionList}
							handleOptionClick={handleDatabaseChange}
							isApiLoading={isApiLoading}
						/>
						{/* Category dropdown */}
						<PRCInventoryLandingHeaderDropDown
							type={CATEGORY}
							value={categorySelectValue}
							optionList={categoryOptionList}
							handleOptionClick={handleCategoryChange}
							isApiLoading={isApiLoading}
						/>
						{/* Search input */}
						<Grid item xs>
							<FormControl
								variant="standard"
								className={styles['prc-landing-search']}
								data-testid="search-test-id"
							>
								<OutlinedInput
									className={styles['prc-landing-search-input']}
									name={refSearchText}
									defaultValue={refSearchText}
									inputRef={searchvalueRef}
									onChange={debouncedSearchHandler}
									id="prc-landing-search-input"
									placeholder={toSentenceCase('Search')}
									startAdornment={
										<InputAdornment position="start">
											<SearchOutlinedIcon />
										</InputAdornment>
									}
									inputProps={{ 'data-testid': 'search-input-test-id' }}
								/>
							</FormControl>
						</Grid>
						{/* Filter button */}
						<Grid item xs="auto">
							<ButtonComp
								btnType="secondary"
								compClass={`${styles['filter-btn']}`}
								data-testid="advancedfilters-icon"
								onClick={handleClickFilterBtn}
							>
								{isApiLoading?.filters ? (
									<Typography>{LOADING}</Typography>
								) : (
									<>
										<div className={styles['filter-icon']}>
											<FilterIcon fill={filterIconColor} />
										</div>
										<Typography>{filterBtnLabel}</Typography>
									</>
								)}
							</ButtonComp>
						</Grid>
						{/* Section tool bar */}
						<SectionToolbar
							types={taxonomySecTools}
							viewType={taxonomyViewType}
							isHeatMapViewVisible={tabIndex !== 4}
							isPrcCardViewEnabled={isPrcCardViewEnabled}
							moreMenuRef={moreMenuRef}
							handleClickMoreMenu={handleClickMoreMenu}
							handleToggleViewType={handleToggleViewType}
							moreMenuAnchorEl={moreMenuAnchorEl}
							handleCloseMoreMenu={handleCloseMoreMenu}
							exportHandler={exportHandler}
							initFunc={sectionToolBarInitFunc}
						/>
					</Grid>
				</Stack>
			</Banner>
		</Stack>
	);
};

export default PRCInventoryLandingHeader;
